import { INDIGO_UPFRONT_CABIN, isIndigoUpfrontCabin, PREMIUM_ECONOMY } from '@/constants/flights/cabinClass'
import { PE_ITINERARY_UPGRADE_SEAT_IMAGE } from '@/constants/flights/premiumEconomy'
import {
    CabinClassSummary,
    CabinUpgradeCallout,
    MediaCarrier,
    MediaResource,
    PromoBanner,
    UpgradeCallout,
} from '@/types/PremiumEconomyApi'

const isMediaResource = (value: unknown): value is MediaResource =>
    !!value && typeof value === 'object' && typeof (value as MediaResource).resourceUrl === 'string'

const toFiniteNumber = (value: unknown): number | null =>
    typeof value === 'number' && isFinite(value) ? value : null

/** resources[] often leads with a video for the PE drawer — not usable as <img src>. */
const VIDEO_URL_RE = /\.(mp4|webm|mov|m3u8)(\?|#|$)/i

export const isVideoUrl = (url?: string | null): boolean => !!url && VIDEO_URL_RE.test(url)

/**
 * Banner hero URL for upgrade banners (<img>).
 * Prefer BE `baseResource` (dedicated hero); skip video URLs from resources[].
 */
export function pickBannerImageUrl(baseResource: string | null, mediaUrls: string[]): string | null {
    if (baseResource && !isVideoUrl(baseResource)) return baseResource
    const firstImage = mediaUrls.find((url) => !!url && !isVideoUrl(url))
    if (firstImage) return firstImage
    return null
}

function pickBannerVideoUrl(baseResource: string | null, mediaUrls: string[]): string | null {
    if (baseResource && isVideoUrl(baseResource)) return baseResource
    return mediaUrls.find((url) => !!url && isVideoUrl(url)) || null
}

const resolveCalloutCaption = (callout: UpgradeCallout | null): string | null => {
    const fromCallout = typeof callout?.caption === 'string' ? callout.caption.trim() : ''
    return fromCallout || null
}

export function resolveMediaUrls(source?: MediaCarrier | null): string[] {
    if (!source || typeof source !== 'object') return []

    const fromResources = Array.isArray(source.resources)
        ? source.resources
              .map(entry => (typeof entry === 'string' ? entry : isMediaResource(entry) ? entry.resourceUrl : ''))
              .filter((url): url is string => !!url)
        : []
    if (fromResources.length) return fromResources

    const fromImages = Array.isArray(source.images) ? source.images.filter((url): url is string => !!url) : []
    if (fromImages.length) return fromImages

    if (typeof source.baseResource === 'string' && source.baseResource) return [source.baseResource]
    return []
}

export function resolveBaseResource(...sources: Array<MediaCarrier | null | undefined>): string | null {
    for (const source of sources) {
        if (source && typeof source.baseResource === 'string' && source.baseResource) return source.baseResource
    }
    return null
}

export function resolveMediaResources(source?: MediaCarrier | null): MediaResource[] {
    if (!source || typeof source !== 'object') return []

    const fromResources: MediaResource[] = Array.isArray(source.resources)
        ? source.resources.reduce<MediaResource[]>((acc, entry) => {
              if (typeof entry === 'string') {
                  if (entry) acc.push({ resourceUrl: entry, displayTitle: '', displaySubTitle: '' })
              } else if (isMediaResource(entry)) {
                  acc.push({
                      resourceUrl: entry.resourceUrl,
                      displayTitle: entry.displayTitle || '',
                      displaySubTitle: entry.displaySubTitle || '',
                  })
              }
              return acc
          }, [])
        : []
    if (fromResources.length) return fromResources

    const fromImages = Array.isArray(source.images)
        ? source.images.filter((url): url is string => !!url).map(url => ({ resourceUrl: url, displayTitle: '', displaySubTitle: '' }))
        : []
    if (fromImages.length) return fromImages

    if (typeof source.baseResource === 'string' && source.baseResource) {
        return [{ resourceUrl: source.baseResource, displayTitle: '', displaySubTitle: '' }]
    }
    return []
}

/** Unwrap the accidental `upgradeCallout: { upgradeCallout: {...} }` nesting (Appendix C.2). */
export function unwrapUpgradeCallout(callout?: UpgradeCallout | null): UpgradeCallout | null {
    if (!callout || typeof callout !== 'object') return null
    const nested = callout.upgradeCallout
    if (nested && typeof nested === 'object') return nested
    return callout
}

/** Fare shape the upgrade-callout resolvers care about. */
export type UpgradeCalloutFareLike = {
    cabinType?: string | null
    upgradeCallout?: UpgradeCallout | null
    [key: string]: any
}

const hasUsableUpgradeCallout = (fare?: UpgradeCalloutFareLike | null): boolean => {
    const callout = unwrapUpgradeCallout(fare?.upgradeCallout)
    if (!callout) return false
    return !fare?.cabinType || callout.targetCabin !== fare.cabinType
}


export function findFareWithUpgradeCallout<T extends UpgradeCalloutFareLike>(
    fares?: Array<T | null | undefined> | null
): T | undefined {
    if (!Array.isArray(fares)) return undefined
    return fares.find((fare): fare is T => hasUsableUpgradeCallout(fare)) || undefined
}

export interface PeUpgradeOffer {
    eligible: boolean
    amount: number
    title: string | null
    baseResource: string | null
    mediaUrls: string[]
    mediaUrl: string | null
    mediaResources: MediaResource[]
    /** BE date-change fee for the banner benefit row; null when not provided. */
    dateChangeFee: number | null
    /** BE-owned benefit bullets when present on the callout. */
    benefits: string[] | null
    /** Display cabin the CTA should open (e.g. INDIGO_UPFRONT / PREMIUM_ECONOMY). */
    targetCabin: string | null
    /** Economy-tab caption. Null when BE omitted it — IU must not fall back to PE copy. */
    caption: string | null
    /** First mp4 from BE resources for the itinerary banner. */
    videoUrl: string | null
}

const EMPTY_OFFER: PeUpgradeOffer = {
    eligible: false,
    amount: 0,
    title: null,
    baseResource: null,
    mediaUrls: [],
    mediaUrl: null,
    mediaResources: [],
    dateChangeFee: null,
    benefits: null,
    targetCabin: null,
    caption: null,
    videoUrl: null,
}

/** Pull a numeric date-change fee from the upgrade callout (tolerates alias keys). */
export function resolveUpgradeDateChangeFee(callout?: UpgradeCallout | null): number | null {
    if (!callout || typeof callout !== 'object') return null
    return (
        toFiniteNumber(callout.dateChangeFee) ??
        toFiniteNumber(callout.amendFee) ??
        toFiniteNumber(callout.dateChangeAmount)
    )
}

const resolveUpgradeBenefits = (callout?: UpgradeCallout | null): string[] | null => {
    if (!callout || !Array.isArray(callout.benefits)) return null
    const list = callout.benefits.filter((b): b is string => typeof b === 'string' && !!b.trim())
    return list.length ? list : null
}

export function resolvePeUpgradeOffer({
    economyFare,
    cabinSummary,
}: {
    economyFare?: { cabinType?: string | null; upgradeCallout?: UpgradeCallout | null } | null
    cabinSummary?: CabinClassSummary | null
}): PeUpgradeOffer {
    const rawCallout = unwrapUpgradeCallout(economyFare?.upgradeCallout)
    const isSelfUpsell =
        !!rawCallout && !!economyFare?.cabinType && rawCallout.targetCabin === economyFare.cabinType
    const callout = isSelfUpsell ? null : rawCallout
    const isIndigo = isIndigoUpfrontCabin(callout?.targetCabin)
    // Same as 7439: PE can still use cabinPromoBanner. IU never invents an offer
    // from promo — title / caption / amount stay on upgradeCallout.
    const promoBanner = isIndigo
        ? cabinSummary?.[INDIGO_UPFRONT_CABIN]?.cabinPromoBanner ?? null
        : cabinSummary?.[PREMIUM_ECONOMY]?.cabinPromoBanner ?? null

    if (!callout && !promoBanner) return EMPTY_OFFER

    let amount = toFiniteNumber(callout?.amount) ?? 0
    if (amount <= 0 && cabinSummary && !isIndigo) {
        const pePrice = toFiniteNumber(cabinSummary[PREMIUM_ECONOMY]?.minCabinPrice ?? null)
        const ecoPrice = toFiniteNumber(cabinSummary.ECONOMY?.minCabinPrice ?? null)
        if (pePrice != null && ecoPrice != null) amount = Math.max(0, Math.round(pePrice - ecoPrice))
    }

    const calloutMedia = resolveMediaUrls(callout)
    const mediaUrls = calloutMedia.length ? calloutMedia : resolveMediaUrls(promoBanner)
    const calloutResources = resolveMediaResources(callout)
    const mediaResources = calloutResources.length ? calloutResources : resolveMediaResources(promoBanner)
    const baseResource = resolveBaseResource(callout, promoBanner)
    // IU renders BE media only — no seat-PNG fallback.
    const bannerImage =
        pickBannerImageUrl(baseResource, mediaUrls) ?? (isIndigo ? null : PE_ITINERARY_UPGRADE_SEAT_IMAGE)

    return {
        eligible: true,
        amount,
        title: callout?.title ?? (isIndigo ? null : promoBanner?.title ?? null),
        baseResource: bannerImage,
        mediaUrls,
        mediaUrl: bannerImage,
        mediaResources,
        dateChangeFee: resolveUpgradeDateChangeFee(callout),
        benefits: resolveUpgradeBenefits(callout),
        targetCabin: callout?.targetCabin ? String(callout.targetCabin) : null,
        caption: resolveCalloutCaption(callout),
        videoUrl: pickBannerVideoUrl(baseResource, mediaUrls),
    }
}

export type ItineraryUpgradeLeg = 'trip' | 'onward' | 'return'

const asCabinClassSummary = (value: unknown): CabinClassSummary | null =>
    value && typeof value === 'object' && !Array.isArray(value) ? (value as CabinClassSummary) : null

/**
 * BE itinerary `FARE_CARDS` — same keys FareFamilyBlock already reads.
 * No remapping and no deep walk.
 */
export function findCabinClassSummary(root: unknown): CabinClassSummary | null {
    if (!root || typeof root !== 'object') return null
    const fareCards = (root as { FARE_CARDS?: Record<string, unknown> }).FARE_CARDS
    return (
        asCabinClassSummary(fareCards?.tripCabinClassSummary) ||
        asCabinClassSummary(fareCards?.onwardCabinClassSummary) ||
        null
    )
}

function buildItineraryOffer(
    callout: UpgradeCallout | null,
    promoBanner: PromoBanner | null = null
): PeUpgradeOffer {
    if (!callout) return EMPTY_OFFER

    const amount = toFiniteNumber(callout.amount) ?? 0
    if (amount <= 0) return EMPTY_OFFER

    const isIndigo = isIndigoUpfrontCabin(callout.targetCabin)
    // IU: cabinPromoBanner is visual fallback only. PE itinerary stays callout-only
    // here; Review still applies the 7439 seat PNG when BE media is missing.
    const fallback = isIndigo ? promoBanner : null
    const calloutMedia = resolveMediaUrls(callout)
    const mediaUrls = calloutMedia.length ? calloutMedia : resolveMediaUrls(fallback)
    const calloutResources = resolveMediaResources(callout)
    const mediaResources = calloutResources.length ? calloutResources : resolveMediaResources(fallback)
    const baseResource = resolveBaseResource(callout, fallback)
    const bannerImage =
        pickBannerImageUrl(baseResource, mediaUrls) ?? (isIndigo ? null : PE_ITINERARY_UPGRADE_SEAT_IMAGE)

    return {
        eligible: true,
        amount,
        title: callout.title ?? null,
        baseResource: bannerImage,
        mediaUrls,
        mediaUrl: bannerImage,
        mediaResources,
        dateChangeFee: resolveUpgradeDateChangeFee(callout),
        benefits: resolveUpgradeBenefits(callout),
        targetCabin: callout.targetCabin ? String(callout.targetCabin) : null,
        caption: resolveCalloutCaption(callout),
        videoUrl: pickBannerVideoUrl(baseResource, mediaUrls),
    }
}

/**
 * BE itinerary `CABIN_UPGRADE_CALLOUT` (`element.data`):
 * `{ upgradeCallout: { tripUpgradeCallout, onwardUpgradeCallout, returnUpgradeCallout } }`
 */
export function resolveItineraryUpgradeOffer(
    cabinUpgradeCallout?: CabinUpgradeCallout | null,
    prefer: ItineraryUpgradeLeg = 'trip',
    cabinSummary?: CabinClassSummary | null
): PeUpgradeOffer {
    const map = cabinUpgradeCallout?.upgradeCallout
    if (!map || typeof map !== 'object') return EMPTY_OFFER

    const preferredFirst =
        prefer === 'onward'
            ? map.onwardUpgradeCallout
            : prefer === 'return'
            ? map.returnUpgradeCallout
            : map.tripUpgradeCallout

    // Honour the requested leg strictly — an onward ask must never fall back to
    // return data (or vice-versa). Only the trip-level ask is allowed to fall
    // back across legs (one-way / special-RT have a single combined callout).
    const candidates =
        prefer === 'onward' || prefer === 'return'
            ? [preferredFirst]
            : [preferredFirst, map.tripUpgradeCallout, map.onwardUpgradeCallout, map.returnUpgradeCallout]

    for (const candidate of candidates) {
        const unwrapped = unwrapUpgradeCallout(candidate)
        const promoBanner = isIndigoUpfrontCabin(unwrapped?.targetCabin)
            ? cabinSummary?.[INDIGO_UPFRONT_CABIN]?.cabinPromoBanner ?? null
            : null
        const offer = buildItineraryOffer(unwrapped, promoBanner)
        if (offer.eligible) return offer
    }
    return EMPTY_OFFER
}
